import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { isUserHasKey } from '../../GlobalFunctions';
import DistrictSelector from '../AdminReportComponents/DistrictSelector';
import CustomDropdown from '../AdminReportComponents/CustomDropdown';
import { Icon } from '@iconify/react';
import { flushLocalForage, getLocalForage, removeLocalForage, setLocalForage } from '../../aaa';
import { useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { SchoolModel } from '@peekapak/types/DataProtocolTypes';
import { getDistricts, getSchools, queryMixpanelByGroupV2 } from '../../BackendInterface';
import Spinner from 'react-spinkit';
import SpinnerOverlay from '../../SpinnerOverlay';
import { lookupNewDBStartDate, getStartOfSchoolYear } from '../reportUtilities';
import { District, ReportingMixpanelEvent, SchoolsList } from '@peekapak/types/ReportDataExchangeTypes';
import DateRangePicker from '../../components/DateRangePicker';
import ChartSegmentElementary from './ChartSegmentElementary';
import ChartSegmentSecondary from './ChartSegmentSecondary';
import styles from './MoodboardAdmin.module.scss';
import { getUpdatedDistrictUnifiedMoodScript, getUpdatedSchoolUnifiedMoodScript } from './moodboardUtilities';

interface MoodboardAdminState {
  dataAvailabilityMessage: string;
  disableChangeLocation?: boolean;
  districtsList: District[] | Record<string, unknown>[];
  districtsListReady: boolean;
  errorMessage: string;
  mixpanelJournalMoodLogs: ReportingMixpanelEvent[];
  mixpanelJournalMoodLogsReady: boolean;
  schoolsList: SchoolsList;
  schoolsListReady: boolean;
  selectedDistrict: string;
  selectedSchool: string;
  selectedStartingDate: string;
  selectedEndingDate: string;
}

const tableOverrideSuffix = '';

const MoodboardDistrictAdmin = (): JSX.Element => {
  const today = new Date();
  const startOfSchoolYear = getStartOfSchoolYear();

  const getCommonInitialState = () => {
    return {
      dataAvailabilityMessage: '',
      disableChangeLocation: true,
      errorMessage: '',
      mixpanelJournalMoodLogs: [],
      mixpanelJournalMoodLogsReady: false,
    };
  };

  const getDistrictInitialState = () => {
    return {
      districtsList: [],
      districtsListReady: false,
      selectedDistrict: '',
    };
  };

  const getSchoolInitialState = () => {
    return {
      schoolsList: {
        district: '',
        list: [],
      },
      schoolsListReady: false,
      selectedSchool: 'All',
    };
  };

  const getDateInitialState = () => {
    return {
      selectedStartingDate: startOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: today.toISOString().split('T')[0],
    };
  };

  const [state, setState] = useState<MoodboardAdminState>({
    ...getCommonInitialState(),
    ...getDistrictInitialState(),
    ...getSchoolInitialState(),
    ...getDateInitialState(),
  });

  const [dateRange, setDateRange] = useState<DateRange>({ from: startOfSchoolYear, to: today });
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);
  const [dateFilteredJournals, setDateFilteredJournals] = useState<ReportingMixpanelEvent[]>([]);
  const [selectedFilterSchool, setSelectedFilterSchool] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState('');

  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const handleBeforeStart = () => {};

  const schoolOptions = state.schoolsListReady
    ? state.schoolsList.list.map((s: SchoolModel) => ({ id: s.id, name: s.name }))
    : [];

  //
  // get list of districts
  //
  useEffect(() => {
    let mounted = true;

    if (!state.districtsListReady) {
      getListOfDistricts();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          districtsList: map,
          districtsListReady: true,
        }));
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          districtsList: mapFromDb,
          districtsListReady: true,
        }));
      }
    }
  }, [state.districtsList, state.districtsListReady, userProfile?.district, state.selectedDistrict]);

  //
  // set selected district
  //
  useEffect(() => {
    run();

    async function run() {
      if (state.districtsListReady && state.districtsList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict = await getLocalForage(storageKey);

        if (selectedDistrict) {
          setState((prevState) => ({ ...prevState, selectedDistrict }));
        } else {
          if (userProfile?.district) {
            if (state.districtsList.map((d) => d.id).includes(userProfile.district)) {
              return setState((prevState) => ({
                ...prevState,
                selectedDistrict: userProfile.district!,
              }));
            }
          }
        }
        return;
      }
      setState((prevState) => ({ ...prevState, selectedDistrict: '' }));
    }
  }, [userProfile?.district, state.districtsListReady, state.districtsList]);

  //
  // schools in district
  //
  useEffect(() => {
    let mounted = true;
    run();
    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (!state.schoolsListReady) await getListOfSchoolsInDistrict();
    }
    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const districtStorageKey = 'selectedDistrict';
      const district =
        state.selectedDistrict || (await getLocalForage(districtStorageKey)) || userProfile?.district || '';
      if (!district) return;
      const storageKey = `${district}-schoolsList`;
      const list = (await getLocalForage(storageKey))?.filter((s: SchoolModel) => !s.isArchived);

      if (list?.length > 0 && mounted) {
        return setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: [{ id: 'All', name: 'All', district: district }, ...list],
          },
          schoolsListReady: true,
        }));
      }

      const listFromDb = (await getSchools(district, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(storageKey, listFromDb, 43200);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: [{ id: 'All', name: 'All', district: district }, ...listFromDb],
          },
          schoolsListReady: true,
        }));
      }
    }
  }, [userProfile, state.schoolsListReady, state.selectedDistrict, state.districtsListReady]);

  //
  // set selected school
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.selectedDistrict && state.schoolsListReady && state.schoolsList.list.length > 0) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        const selectedSchool = await (async () => {
          if (!userProfile?.district) {
            return '';
          }

          if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
        })();

        setState((prevState) => ({ ...prevState, selectedSchool }));
        if (selectedSchool !== 'All') {
          const schoolName = state.schoolsList.list.filter((s: SchoolModel) => s.id === selectedSchool)[0]?.name;
          setSchoolName(schoolName);
        }

        if (isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
          if (state.schoolsListReady) {
            if (userProfile?.schoolId && state.schoolsList.list.map((s) => s.id).includes(userProfile.schoolId)) {
              return (await getLocalForage(storageKey)) || userProfile.schoolId;
            }

            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          return '';
        }

        if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
          return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
        }

        return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
      }

      setState((prevState) => ({ ...prevState, selectedSchool: 'All' }));
    }
  }, [
    state.schoolsListReady,
    state.schoolsList.list,
    state.selectedDistrict,
    userProfile?.district,
    userProfile?.schoolId,
  ]);

  //
  // get new mixpanel DB mood events for selected district
  //
  useEffect(() => {
    let mounted = true;
    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;
    const storageKey = `${state.selectedDistrict}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedDistrict && state.districtsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              mixpanelJournalMoodLogs: data,
              mixpanelJournalMoodLogsReady: true,
            }));
          }
        }
      }
    }
    async function doQuery() {
      try {
        const data = await queryMixpanelByGroupV2(
          getUpdatedDistrictUnifiedMoodScript(selectedStartingDate, selectedEndingDate, state.selectedDistrict),
          'unified',
          tableOverrideSuffix,
        );
        await setLocalForage(storageKey, data, 60);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            mixpanelJournalMoodLogs: data,
            mixpanelJournalMoodLogsReady: true,
          }));
        }
      } catch (error) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            errorMessage: (error as Error).message,
          }));
        }
      }
    }
  }, [state.selectedDistrict, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // disable drop downs when data is being retrieved
  //
  useEffect(() => {
    if (
      (!state.districtsListReady || !state.mixpanelJournalMoodLogsReady || !state.schoolsListReady) &&
      !state.errorMessage
    ) {
      return setState((prevState) => ({
        ...prevState,
        disableChangeLocation: true,
      }));
    }

    return setState((prevState) => ({
      ...prevState,
      disableChangeLocation: false,
    }));
  }, [state.districtsListReady, state.schoolsListReady, state.mixpanelJournalMoodLogsReady, state.errorMessage]);

  //
  // set display of data availablity message
  //
  useEffect(() => {
    if (state.mixpanelJournalMoodLogsReady && !state.errorMessage) {
      const journalMoodLogStartDate = lookupNewDBStartDate('journalMoodLog');
      if (dateRange.from && dateRange.from < journalMoodLogStartDate) {
        return setState((prevState) => ({
          ...prevState,
          dataAvailabilityMessage: 'Student mood data for this district is only available after August 2023.',
        }));
      }
    }
  }, [dateRange, state.mixpanelJournalMoodLogs, state.mixpanelJournalMoodLogsReady]);

  //
  // set error conditions
  //
  useEffect(() => {
    if (userProfile && !userProfile.district && !isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (userProfile?.district && !isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL') && !userProfile.schoolId) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (state.schoolsListReady && state.schoolsList.list.length === 0) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No school data applicable',
      }));
    }
  }, [userProfile, state.districtsListReady, state.schoolsListReady, state.schoolsList, state.selectedDistrict]);

  const getMixpanelJournalMoodLogsForSchool = async (schoolId: string) => {
    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;

    const storageKey = `${schoolId}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;

    if (schoolId && state.schoolsListReady) {
      const data = await getLocalForage(storageKey);
      if (!data) {
        doQuery();
      } else {
        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: data,
          mixpanelJournalMoodLogsReady: true,
        }));
      }
    }
    async function doQuery() {
      const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === schoolId);

      if (!selectedSchoolObject)
        throw new Error(`Unexpected condition: could not find a matching school object for selected school in report`);

      try {
        const result = await queryMixpanelByGroupV2(
          getUpdatedSchoolUnifiedMoodScript(
            selectedStartingDate,
            selectedEndingDate,
            selectedSchoolObject.combineIds || selectedSchoolObject.id,
          ),
          'unified',
          tableOverrideSuffix,
        );

        await setLocalForage(storageKey, result, 60);

        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: result,
          mixpanelJournalMoodLogsReady: true,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          errorMessage: (error as Error).message,
        }));
      }
    }
  };

  useEffect(() => {
    if (state.mixpanelJournalMoodLogsReady) {
      const journals = (state.mixpanelJournalMoodLogs as unknown as Record<string, unknown>[]).filter(
        (e: Record<string, unknown>) => {
          const createdAt = e.createdAt as string | undefined;
          if (!createdAt || !dateRange.from || !dateRange.to) return false;
          const d = new Date(createdAt);
          return d >= dateRange.from && d <= dateRange.to;
        },
      ) as unknown as ReportingMixpanelEvent[];
      setDateFilteredJournals(journals);
    }
  }, [dateRange, state.mixpanelJournalMoodLogs, state.mixpanelJournalMoodLogsReady]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      selectedStartingDate: dateRange.from
        ? dateRange.from.toISOString().split('T')[0]
        : startOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: dateRange.to ? dateRange.to.toISOString().split('T')[0] : today.toISOString().split('T')[0],
    }));
    setSelectedFilterSchool(null);
  }, [dateRange.from?.getTime(), dateRange.to?.getTime()]);

  async function onSelectDistrict(eventKey: string | null) {
    if (state.districtsListReady && state.districtsList.length > 0) {
      const selectedDistrict = eventKey || '';
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedDistrict,
      }));
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, selectedDistrict);
      await removeLocalForage(`${selectedDistrict}-selectedSchool`);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onReload() {
    await flushLocalForage();
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getDateInitialState(),
      ...getDistrictInitialState(),
      ...getSchoolInitialState(),
    }));
  }

  const handleFilterSchoolChange = (schoolId: string | null) => {
    const id = schoolId || 'All';
    setSelectedFilterSchool(id);
    setState((prevState) => ({
      ...prevState,
      selectedSchool: id,
      mixpanelJournalMoodLogs: [],
      mixpanelJournalMoodLogsReady: false,
    }));
    if (id === 'All') {
      const selectedStartingDate = state.selectedStartingDate;
      const selectedEndingDate = state.selectedEndingDate;
      const storageKey = `${state.selectedDistrict}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;
      (async () => {
        const data = await getLocalForage(storageKey);
        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: data,
          mixpanelJournalMoodLogsReady: true,
        }));
        setSchoolName('');
      })();
    } else {
      setSchoolName(state.schoolsList.list.find((s: SchoolModel) => s.id === id)?.name || '');
      getMixpanelJournalMoodLogsForSchool(id);
    }
  };

  const getElementaryData = () => {
    return dateFilteredJournals.filter((e: Record<string, unknown>) => parseInt(e.grade as string, 10) <= 5);
  };

  const getSecondaryData = () => {
    return dateFilteredJournals.filter((e: Record<string, unknown>) => parseInt(e.grade as string, 10) >= 6);
  };

  if (!userProfile || !state.districtsListReady || !state.schoolsListReady) {
    return <SpinnerOverlay isShow />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.districtAndSchoolSelectors}>
          <div className={styles.titleAndReloadButton}>
            <h1>District Wide Mood Check-in</h1>
            <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
            <div className={styles.schoolDropdownWrapper}>
              <CustomDropdown
                list={schoolOptions}
                onSelectSubject={handleFilterSchoolChange}
                initialValue={schoolOptions.length > 0 ? schoolOptions[0].id : 'All'}
                currentlySelectedItem={selectedFilterSchool ?? state.selectedSchool}
                disableChangeLocation={!state.schoolsListReady}
                icon='mdi:school'
                placeholder='Select a school'
              />
            </div>
          </div>
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={state.districtsList}
            onReload={onReload}
            onSelectDistrict={onSelectDistrict}
            initialValue={state.selectedDistrict}
            currentlySelectedDistrict={state.selectedDistrict}
            disableChangeLocation={state.disableChangeLocation || false}
          />
        </div>
        <div>
          <DateRangePicker
            disabled={state.disableChangeLocation || false}
            dateRange={dateRange}
            setDateRange={setDateRange}
            startDate={startOfSchoolYear}
            excludeAfter={today}
            beforeFirstInputText='Start Date:'
            beforeSecondInputText='End Date:'
            isDisplayCalendar={isDisplayDayPicker}
            setIsDisplayCalendar={setIsDisplayDayPicker}
            handleBeforeStart={handleBeforeStart}
          />
        </div>
        {!state.mixpanelJournalMoodLogsReady || state.disableChangeLocation ? (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.chartsContainer}>
            <ChartSegmentElementary
              data={getElementaryData()}
              dataAvailabilityMessage={state.dataAvailabilityMessage}
              previousPeriodData={[]}
              dateRange={{ from: dateRange.from || startOfSchoolYear, to: dateRange.to || today }}
              schoolName={schoolName}
              selectedPeriod='yearly'
              shiftPeriod={() => {}}
              shouldShowNavArrowLeft={() => false}
              shouldShowNavArrowRight={() => false}
            />
            <ChartSegmentSecondary
              data={getSecondaryData()}
              dataAvailabilityMessage={state.dataAvailabilityMessage}
              previousPeriodData={[]}
              dateRange={{ from: dateRange.from || startOfSchoolYear, to: dateRange.to || today }}
              schoolName={schoolName}
              selectedPeriod='yearly'
              shiftPeriod={() => {}}
              shouldShowNavArrowLeft={() => false}
              shouldShowNavArrowRight={() => false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodboardDistrictAdmin;
